import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy, FloppyDisk, X, Camera, MagicWand } from "@phosphor-icons/react";

/**
 * Planilla — Vista de tabla 10 jugadores × 10 puntos + total + ranking.
 * Solo para admin/directivo/profesor.
 * Props: reta_id, court_idx, court_name, registeredUsers (array {id, name})
 */
export default function PlanillaPorCancha({ retaId, courtIdx, courtName, onClose, registeredUsers = [] }) {
  const [playerSlots, setPlayerSlots] = useState(30);
  const [pendingSlots, setPendingSlots] = useState(30);
  const [players, setPlayers] = useState(
    Array.from({ length: 30 }, () => ({ name: "", user_id: null, is_guest: false, points: Array(10).fill(0), total: 0, rank: null }))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [updatingSlots, setUpdatingSlots] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/retas/${retaId}/courts/${courtIdx}/planilla`);
      const slots = Math.max(1, Math.min(50, parseInt(r.data?.player_slots, 10) || 30));
      setPlayerSlots(slots);
      setPendingSlots(slots);
      const list = r.data?.planilla?.players || [];
      const padded = [...list];
      while (padded.length < slots) padded.push({ name: "", user_id: null, is_guest: false, points: Array(10).fill(0), total: 0, rank: null });
      setPlayers(padded.slice(0, slots));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar planilla");
    } finally { setLoading(false); }
  }, [retaId, courtIdx]);

  useEffect(() => { load(); }, [load]);

  const applySlots = async () => {
    const next = Math.max(1, Math.min(50, parseInt(pendingSlots, 10) || 30));
    setUpdatingSlots(true);
    try {
      await api.put(`/retas/${retaId}/courts/${courtIdx}/planilla/slots`, { player_slots: next });
      toast.success(`Plantilla ajustada a ${next} jugadores`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ajustar plantilla");
    } finally { setUpdatingSlots(false); }
  };

  const add10Slots = () => {
    const next = Math.min(50, playerSlots + 10);
    setPendingSlots(next);
    // auto-apply for convenience
    (async () => {
      setUpdatingSlots(true);
      try {
        await api.put(`/retas/${retaId}/courts/${courtIdx}/planilla/slots`, { player_slots: next });
        toast.success(`+10 lugares · total ${next}`);
        await load();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al agregar lugares");
      } finally { setUpdatingSlots(false); }
    })();
  };

  const updatePlayer = (idx, field, value) => {
    setPlayers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const updatePoint = (idx, pIdx, val) => {
    const num = Math.max(0, Math.min(99, parseInt(val) || 0));
    setPlayers((prev) => {
      const next = [...prev];
      const pts = [...next[idx].points];
      pts[pIdx] = num;
      next[idx] = { ...next[idx], points: pts, total: pts.reduce((a, b) => a + b, 0) };
      return next;
    });
  };

  const selectRegistered = (idx, userId) => {
    if (userId === "__guest__") {
      updatePlayer(idx, "user_id", null);
      updatePlayer(idx, "is_guest", true);
    } else if (userId === "__clear__") {
      setPlayers((prev) => {
        const next = [...prev];
        next[idx] = { name: "", user_id: null, is_guest: false, points: Array(10).fill(0), total: 0, rank: null };
        return next;
      });
    } else if (userId.startsWith("__guest_court_")) {
      // Picked an invited player from the court (no user_id, only name)
      const guestName = userId.replace("__guest_court_", "");
      setPlayers((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], user_id: null, name: guestName, is_guest: true };
        return next;
      });
    } else {
      const u = registeredUsers.find((x) => x.id === userId);
      if (u) {
        setPlayers((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], user_id: u.id, name: u.name, is_guest: false };
          return next;
        });
      }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/retas/${retaId}/courts/${courtIdx}/planilla`, { players });
      toast.success("Planilla guardada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result || "";
      const b64 = String(dataUrl).split(",")[1] || "";
      resolve({ b64, mime: file.type || "image/jpeg" });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Resize image to max 1600px on the longer side (keeps OCR-readable text, reduces payload)
  const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) { resolve(file); return; }
        resolve(new File([blob], file.name || "planilla.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.85);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

  const handleImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset for re-selection
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Solo JPEG, PNG o WEBP");
      return;
    }
    setAnalyzing(true);
    try {
      const compressed = await compressImage(file);
      const { b64, mime } = await fileToBase64(compressed);
      const res = await api.post(
        `/retas/${retaId}/courts/${courtIdx}/planilla/analyze-image`,
        { image_base64: b64, mime },
        { timeout: 60000 }
      );
      const aiPlayers = res.data?.players || [];
      if (!aiPlayers.length) {
        toast.error("No se detectaron jugadores en la imagen");
        return;
      }
      // Merge into current state — fill rows top-down respetando el cupo configurado
      const padded = [...aiPlayers];
      while (padded.length < playerSlots) {
        padded.push({ name: "", user_id: null, is_guest: false, points: Array(10).fill(0), total: 0, rank: null });
      }
      setPlayers(padded.slice(0, playerSlots));
      toast.success(`Detectados ${aiPlayers.length} jugador${aiPlayers.length !== 1 ? "es" : ""}. Revisa y guarda.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar imagen");
    } finally {
      setAnalyzing(false);
    }
  };

  // Compute ranking visualization (winners get medal colors)
  const ranked = [...players.map((p, i) => ({ ...p, idx: i }))]
    .filter((p) => p.name || p.user_id)
    .sort((a, b) => b.total - a.total);
  const rankByIdx = {};
  ranked.forEach((p, r) => { rankByIdx[p.idx] = r + 1; });

  return (
    <div className="space-y-3" data-testid="planilla-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-1"><Trophy size={12} weight="fill" /> Planilla por Cancha</p>
          <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tight text-white">{courtName || `Cancha ${courtIdx + 1}`}</h2>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex items-end gap-1" data-testid="planilla-slots-controls">
            <div>
              <label className="block text-[9px] tracking-[0.15em] uppercase text-gray-400 mb-0.5">Lugares</label>
              <Input
                type="number"
                min={1}
                max={50}
                value={pendingSlots}
                onChange={(e) => setPendingSlots(e.target.value)}
                className="bg-[#0A0A0A] border-white/15 h-8 w-16 text-center font-bold"
                data-testid="planilla-slots-input"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={applySlots}
              disabled={updatingSlots || loading || parseInt(pendingSlots, 10) === playerSlots}
              variant="outline"
              className="border-white/15 h-8 text-[10px] uppercase font-bold"
              data-testid="planilla-slots-apply"
            >
              Aplicar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={add10Slots}
              disabled={updatingSlots || loading || playerSlots >= 50}
              variant="outline"
              className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 h-8 text-[10px] uppercase font-bold"
              data-testid="planilla-add-10"
            >
              + 10 lugares
            </Button>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={handleImageSelected}
            className="hidden"
            data-testid="planilla-camera-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageSelected}
            className="hidden"
            data-testid="planilla-file-input"
          />
          <Button onClick={() => cameraInputRef.current?.click()} disabled={analyzing} variant="outline" className="border-[#00A0FF]/40 text-[#00A0FF] hover:bg-[#00A0FF]/10 font-bold uppercase text-xs" data-testid="planilla-take-photo-btn">
            <Camera size={14} className="mr-1" /> {analyzing ? "Analizando..." : "Tomar foto"}
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} disabled={analyzing} variant="outline" className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10 font-bold uppercase text-xs" data-testid="planilla-upload-image-btn">
            <MagicWand size={14} className="mr-1" /> {analyzing ? "Analizando..." : "Subir imagen IA"}
          </Button>
          <Button onClick={save} disabled={saving || loading || analyzing} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="planilla-save">
            <FloppyDisk size={14} className="mr-1" /> {saving ? "Guardando..." : "Guardar"}
          </Button>
          {onClose && <Button onClick={onClose} variant="outline" className="border-white/15" data-testid="planilla-close-btn"><X size={14} /></Button>}
        </div>
      </div>

      {analyzing && (
        <div className="p-3 bg-[#00A0FF]/10 border border-[#00A0FF]/30 rounded text-[11px] text-[#00A0FF] flex items-center gap-2" data-testid="planilla-analyzing">
          <div className="w-3 h-3 border-2 border-[#00A0FF] border-t-transparent animate-spin rounded-full" />
          <span>Analizando la imagen con IA (Claude Sonnet 4.5)... esto puede tomar 10-30 segundos.</span>
        </div>
      )}

      <div className="overflow-x-auto border border-white/10 rounded">
        <table className="w-full text-xs" data-testid="planilla-table">
          <thead className="bg-white/5 text-[9px] tracking-[0.15em] uppercase font-bold text-gray-400">
            <tr>
              <th className="px-1.5 py-2 text-center w-8">#</th>
              <th className="px-2 py-2 text-left min-w-[180px]">Jugador</th>
              {Array.from({ length: 10 }, (_, i) => (
                <th key={i} className="px-1 py-2 text-center w-10 border-l border-white/5">{i + 1}</th>
              ))}
              <th className="px-2 py-2 text-center w-16 border-l border-[#CCFF00]/30 bg-[#CCFF00]/5 text-[#CCFF00]">Total</th>
              <th className="px-2 py-2 text-center w-14 border-l border-white/5">Rank</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p, idx) => {
              const rank = rankByIdx[idx];
              const medalClass = rank === 1 ? "text-yellow-400" : rank === 2 ? "text-gray-300" : rank === 3 ? "text-orange-400" : "text-gray-500";
              return (
                <tr key={idx} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`planilla-row-${idx}`}>
                  <td className="px-1.5 py-1 text-center font-bold text-gray-500">{idx + 1}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <Select value={p.user_id || (p.is_guest && p.name ? `__guest_court_${p.name}` : p.is_guest ? "__guest__" : "")} onValueChange={(v) => selectRegistered(idx, v)}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-7 text-[10px] flex-shrink-0 w-32" data-testid={`planilla-select-${idx}`}>
                          <SelectValue placeholder="Elegir" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-64">
                          <SelectItem value="__guest__">+ Invitado libre</SelectItem>
                          <SelectItem value="__clear__">— Limpiar</SelectItem>
                          {registeredUsers.length > 0 && (
                            <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-[#CCFF00]/70 font-bold">De esta cancha</div>
                          )}
                          {registeredUsers.filter((u) => u.id).map((u) => (
                            <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}{u.is_guest ? " (inv)" : ""}</SelectItem>
                          ))}
                          {registeredUsers.filter((u) => !u.id && u.name).map((u, i) => (
                            <SelectItem key={`guest-court-${i}-${u.name}`} value={`__guest_court_${u.name}`} className="text-xs text-yellow-300">{u.name} (inv)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={p.name}
                        onChange={(e) => updatePlayer(idx, "name", e.target.value)}
                        placeholder={p.is_guest ? "Invitado..." : "—"}
                        className="bg-transparent border-white/10 h-7 text-[11px] flex-1 min-w-0"
                        disabled={!!p.user_id}
                        maxLength={80}
                        data-testid={`planilla-name-${idx}`}
                      />
                    </div>
                  </td>
                  {p.points.map((pt, pIdx) => (
                    <td key={pIdx} className="px-1 py-1 border-l border-white/5">
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={pt || ""}
                        onChange={(e) => updatePoint(idx, pIdx, e.target.value)}
                        className="w-8 h-7 bg-transparent border border-white/10 text-center text-[11px] focus:border-[#CCFF00]/40 focus:outline-none"
                        data-testid={`planilla-pt-${idx}-${pIdx}`}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center border-l border-[#CCFF00]/30 bg-[#CCFF00]/5">
                    <span className="font-['Barlow_Condensed'] text-xl font-black text-[#CCFF00]">{p.total}</span>
                  </td>
                  <td className="px-2 py-1 text-center border-l border-white/5">
                    {rank ? (
                      <span className={`inline-block w-7 h-7 rounded-full border-2 ${rank <= 3 ? "border-current " + medalClass : "border-gray-500 text-gray-500"} font-['Barlow_Condensed'] text-base font-black leading-none flex items-center justify-center`}>
                        {rank}
                      </span>
                    ) : (
                      <span className="text-gray-700">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid sm:grid-cols-3 gap-2 text-[10px]">
        <div className="bg-amber-400/10 border border-amber-400/30 p-2 rounded">
          <div className="font-bold text-amber-300 uppercase tracking-wider mb-0.5">🥇 1er lugar</div>
          <div className="text-gray-300">+20 DYD bonus</div>
        </div>
        <div className="bg-gray-400/10 border border-gray-400/30 p-2 rounded">
          <div className="font-bold text-gray-300 uppercase tracking-wider mb-0.5">🥈 2do lugar</div>
          <div className="text-gray-300">+10 DYD bonus</div>
        </div>
        <div className="bg-orange-700/15 border border-orange-700/40 p-2 rounded">
          <div className="font-bold text-orange-300 uppercase tracking-wider mb-0.5">🥉 3er lugar</div>
          <div className="text-gray-300">+5 DYD bonus</div>
        </div>
      </div>

      <p className="text-[10px] text-gray-500">
        💡 Los puntos se suman y aparecen en el Ranking de la cancha y en el Ranking General. Los DYD se acreditan en automático (1 DYD por punto + bonus de podio).
      </p>
    </div>
  );
}
